'use client';

import { useState, type FC, type FormEvent, useContext } from 'react';
import { useSearchParams } from 'next/navigation';
import { changeLoginMethod } from '@/_api/users';
import {
  Button,
  ButtonGroup,
  Flex,
  Input,
  Text,
  Separator,
} from '@/_components';
import { LoginContext } from '@/_contexts/LoginProvider';
import type { SentCodeProps } from './SentCode.types';
import { ToastContext } from '@/_contexts/ToastProvider';

import { useSignInWithCode } from '@/_hooks/useSignInWithCode';

export const SentCode: FC<SentCodeProps> = ({ method }) => {
  const [verificationCode, setVerificationCode] = useState<string>('');
  const { addToast } = useContext(ToastContext);
  const { previousLoginState, setLoginState, username, phoneNumber, uid } =
    useContext(LoginContext);
  const searchParams = useSearchParams();

  const { isLoading, signInWithVerificationCode } = useSignInWithCode();

  const onSubmit = async (event: FormEvent) => {
    event?.preventDefault?.();
    (globalThis as any).IS_LOGIN_ATTEMPT = true;

    signInWithVerificationCode(verificationCode);
  };

  const onSetValue = (event: any) => {
    setVerificationCode(event?.target.value);
  };

  const onBack = () => {
    if (previousLoginState === 'expired-link') {
      return setLoginState('expired-link');
    }

    setLoginState('login');
  };

  const onUseAlternateSignIn = async () => {
    const redirect = searchParams.get('redirect');

    try {
      const response = (await changeLoginMethod({
        uid,
        username,
        redirect,
      })) as unknown as Response; // TODO: `_callBackend` refactoring needed. See SOR-13473.

      const { status } = response;

      if (status === 429) {
        const data = await response.json();
        const message = data?.message;
        addToast('error', message);
        return;
      }

      setLoginState('alternate-sign-in');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <Flex isColumn={true} gap={8} padding={10}>
        <Input
          label="One time password"
          isLabelHidden={true}
          type="text"
          placeholder="Your code"
          maxLength={6}
          value={verificationCode}
          onChange={onSetValue}
        />
        <ButtonGroup align="right">
          <Button
            value="Back"
            type="button"
            isDisabled={isLoading}
            intent="secondary"
            onClick={onBack}
          />
          <Button
            value="Verify Code"
            isLoading={isLoading}
            isDisabled={isLoading || verificationCode?.length !== 6}
            type="submit"
            intent="primary"
          />
        </ButtonGroup>
        <Separator isLight={true} />
        <Text type="small" color="secondary" isBreakAll={true}>
          Sent a six-digit code to your saved{' '}
          {method === 'text' ? (
            <>
              phone number:
              <br />
              <b>(***) ***-{phoneNumber || '****'}</b>
            </>
          ) : (
            <>
              email address:
              <br />
              <b>{username}</b>
            </>
          )}
        </Text>
        <Separator isLight={true} />
        <Button
          value="Use Alternate Sign-in"
          intent="secondary"
          type="button"
          onClick={onUseAlternateSignIn}
          isDisabled={isLoading}
        />
      </Flex>
    </form>
  );
};
